import { Button, Icon, Label, ScrollArea, SegmentedButtons, Tooltip, token } from "@may-i/mash-design-system";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import type { CameraMemo, CameraMemoType } from "~/entities/camera-memo/types";
import { DeleteConfirmDialog } from "~/pages/resource-management/DeleteConfirmDialog";
import { IcDelete } from "~/shared/assets/icons/IcDelete";

interface MemoPanelProps {
  memos: CameraMemo[];
  selectedCameraId: number | null;
  cameraDisplayName: string | null;
  selectedFloorPlanId: number | null;
  selectedMemoId: number | null;
  placeIcon?: ReactNode;
  isPlacedOnContext?: (memo: CameraMemo) => boolean;
  placementLabels?: { add: string; remove: string };
  /** mount 시 자동 add 진입 여부. 마커 toolbar "메모 추가" 등 add 의도일 때만 true. 카드 메모 아이콘처럼 조회 의도면 false. (PO QA 3차 w-3) */
  addOnMount?: boolean;
  /** addOnMount 소비 후 부모가 flag 를 false 로 되돌리도록 호출 */
  onConsumeAddOnMount?: () => void;
  /** 새 메모 생성 시 기본 타입. 추가 카메라면 'adjustmentOrAddition' 을 내려줌 (F7). 기본 'default'. */
  defaultNewMemoType?: CameraMemoType;
  onMemoSelect?: (memoId: number | null) => void;
  onSave: (actions: MemoAction[]) => void;
  onClose: () => void;
  onToggleMemoOnFloorPlan: (memo: CameraMemo) => void;
}

export type MemoAction =
  | { actionType: "create"; data: Omit<CameraMemo, "id" | "updatedAt"> }
  | { actionType: "update"; data: CameraMemo }
  | { actionType: "delete"; data: CameraMemo };

interface LocalMemo {
  localId: string;
  memo: CameraMemo | null;
  content: string;
  type: CameraMemoType;
  isEditing: boolean;
  isNew: boolean;
  isDeleted: boolean;
}

export function MemoPanel({
  memos,
  selectedCameraId,
  cameraDisplayName,
  selectedFloorPlanId,
  selectedMemoId,
  placeIcon,
  isPlacedOnContext,
  placementLabels,
  addOnMount,
  onConsumeAddOnMount,
  defaultNewMemoType = "default",
  onMemoSelect,
  onSave,
  onClose,
  onToggleMemoOnFloorPlan,
}: MemoPanelProps) {
  const { t } = useTranslation("camera-mapping");

  const [localMemos, setLocalMemos] = useState<LocalMemo[]>([]);
  const prevCameraIdRef = useRef<number | null>(null);
  // 새로 생성된 메모의 localId — 다음 render 후 textarea 에 focus 주기 위함
  const [pendingFocusLocalId, setPendingFocusLocalId] = useState<string | null>(null);
  const textareaRefs = useRef<Map<string, HTMLTextAreaElement>>(new Map());

  useEffect(() => {
    const cameraChanged = selectedCameraId !== prevCameraIdRef.current;
    prevCameraIdRef.current = selectedCameraId;

    const cameraMemos = selectedCameraId
      ? memos
          .filter((m) => m.cameraId === selectedCameraId)
          .sort((a, b) => b.updatedAt - a.updatedAt)
      : [];

    if (cameraChanged) {
      // 카메라 변경 시 완전 리셋
      setLocalMemos(
        cameraMemos.map((m) => ({
          localId: `server-${m.id}`,
          memo: m,
          content: m.content,
          type: m.type,
          isEditing: false,
          isNew: false,
          isDeleted: false,
        })),
      );
    } else {
      // 같은 카메라 — memos prop만 변경 (pending 반영 등)
      // 편집 중인 로컬 상태를 유지하면서 서버 데이터를 병합
      setLocalMemos((prev) => {
        const prevMap = new Map(prev.map((lm) => [lm.localId, lm]));
        const merged: LocalMemo[] = cameraMemos.map((m) => {
          const localId = `server-${m.id}`;
          const existing = prevMap.get(localId);
          if (existing && (existing.isEditing || existing.isDeleted)) {
            // 편집/삭제 중인 항목은 로컬 상태 유지
            return { ...existing, memo: m };
          }
          return {
            localId,
            memo: m,
            content: m.content,
            type: m.type,
            isEditing: false,
            isNew: false,
            isDeleted: false,
          };
        });
        // 로컬에만 있는 새 메모(isNew) 유지
        for (const lm of prev) {
          if (lm.isNew && !lm.isDeleted) {
            merged.unshift(lm);
          }
        }
        return merged;
      });
    }
  }, [memos, selectedCameraId]);

  const filteredMemos = localMemos.filter((lm) => !lm.isDeleted);

  const isAnyEditing = localMemos.some((lm) => lm.isEditing && !lm.isDeleted);

  const handleAddMemo = useCallback(() => {
    if (!selectedCameraId) return;
    const localId = `new-${Date.now()}`;
    const newMemo: LocalMemo = {
      localId,
      memo: null,
      content: "",
      type: defaultNewMemoType,
      isEditing: true,
      isNew: true,
      isDeleted: false,
    };
    setLocalMemos((prev) => [newMemo, ...prev]);
    setPendingFocusLocalId(localId);
  }, [selectedCameraId, defaultNewMemoType]);

  // mount 시 add 의도 (마커 toolbar "메모 추가") 면 자동 add + focus. 조회 의도(카드 아이콘) 면 skip.
  // (PO QA 3차 w-3 — 이전엔 signal 값 기반이어서 stale signal 로 view 진입 시 add 발동)
  useEffect(() => {
    if (!addOnMount) return;
    handleAddMemo();
    onConsumeAddOnMount?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 새로 추가된 메모 textarea 에 focus
  useEffect(() => {
    if (!pendingFocusLocalId) return;
    const el = textareaRefs.current.get(pendingFocusLocalId);
    if (el) {
      el.focus();
      setPendingFocusLocalId(null);
    }
  }, [pendingFocusLocalId, localMemos]);

  const handleContentChange = useCallback((localId: string, content: string) => {
    setLocalMemos((prev) =>
      prev.map((lm) => (lm.localId === localId ? { ...lm, content } : lm)),
    );
  }, []);

  const handleTypeChange = useCallback((localId: string, type: CameraMemoType) => {
    setLocalMemos((prev) =>
      prev.map((lm) => (lm.localId === localId ? { ...lm, type } : lm)),
    );
  }, []);

  const handleStartEdit = useCallback((localId: string) => {
    setLocalMemos((prev) =>
      prev.map((lm) =>
        lm.localId === localId ? { ...lm, isEditing: true } : lm,
      ),
    );
  }, []);

  const handleCancelEdit = useCallback((localId: string) => {
    setLocalMemos((prev) =>
      prev.flatMap((lm) => {
        if (lm.localId !== localId) return [lm];
        // New memo: just remove it
        if (lm.isNew) return [];
        // Existing memo: revert to server state
        return [{ ...lm, content: lm.memo!.content, type: lm.memo!.type, isEditing: false }];
      }),
    );
  }, []);

  const handleDelete = useCallback((localId: string) => {
    setLocalMemos((prev) =>
      prev.map((lm) =>
        lm.localId === localId ? { ...lm, isDeleted: true } : lm,
      ),
    );
  }, []);

  const hasMemoChanges = useCallback((lm: LocalMemo) => {
    const trimmed = lm.content.trim();
    if (lm.isNew) return trimmed.length > 0;
    if (!lm.memo) return false;
    // 공백만 남은 경우는 저장 불가 (서버에 빈 문자열을 들이박지 않음)
    if (!trimmed) return false;
    return trimmed !== lm.memo.content || lm.type !== lm.memo.type;
  }, []);

  const handleSaveMemo = useCallback((localId: string) => {
    if (!selectedCameraId) return;
    const lm = localMemos.find((m) => m.localId === localId);
    if (!lm) return;

    const actions: MemoAction[] = [];

    const trimmedContent = lm.content.trim();

    if (lm.isNew && trimmedContent) {
      actions.push({
        actionType: "create",
        data: {
          cameraId: selectedCameraId,
          type: lm.type,
          content: trimmedContent,
          floorPlanId: null,
          positionForFloorPlan: null,
          createdAt: null,
        },
      });
    } else if (lm.memo && trimmedContent && (trimmedContent !== lm.memo.content || lm.type !== lm.memo.type)) {
      actions.push({
        actionType: "update",
        data: { ...lm.memo, content: trimmedContent, type: lm.type },
      });
    }

    if (actions.length > 0) {
      onSave(actions);
      // 새 메모는 pending 반영 시 effectiveMemos에서 임시 ID로 추가되므로 로컬에서 제거
      if (lm.isNew) {
        setLocalMemos((prev) => prev.filter((m) => m.localId !== localId));
      } else {
        // 기존 메모는 편집 종료 + 로컬 데이터 갱신
        setLocalMemos((prev) =>
          prev.map((m) =>
            m.localId === localId
              ? { ...m, memo: { ...m.memo!, content: lm.content, type: lm.type }, isEditing: false }
              : m,
          ),
        );
      }
    }
  }, [localMemos, selectedCameraId, onSave]);

  const [pendingDeleteLocalId, setPendingDeleteLocalId] = useState<string | null>(null);

  const handleDeleteMemo = useCallback((localId: string) => {
    if (!selectedCameraId) return;
    const lm = localMemos.find((m) => m.localId === localId);
    if (!lm) return;

    if (lm.isNew) {
      // 신규 (서버 미저장) — 확인 없이 로컬 상태에서 즉시 제거
      setLocalMemos((prev) => prev.filter((m) => m.localId !== localId));
      return;
    }

    // 기존 메모 영구 삭제 — 확인 다이얼로그 (도면 위 메모 삭제와 일관성)
    setPendingDeleteLocalId(localId);
  }, [localMemos, selectedCameraId]);

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDeleteLocalId) return;
    const lm = localMemos.find((m) => m.localId === pendingDeleteLocalId);
    if (lm?.memo) {
      onSave([{ actionType: "delete", data: lm.memo }]);
    }
    handleDelete(pendingDeleteLocalId);
    setPendingDeleteLocalId(null);
  }, [pendingDeleteLocalId, localMemos, onSave, handleDelete]);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>{t("memo.title")}</Title>
          {cameraDisplayName && <CountLabel>{t("memo.cameraLabel", { name: cameraDisplayName })}</CountLabel>}
        </HeaderLeft>
        <HeaderRight>
          <Button
            variant="outlined"
            color="primary"
            size="xSmall"
            disabled={isAnyEditing}
            onClick={handleAddMemo}
          >
            {t("memo.addMemo")}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="xSmall"
            onClick={onClose}
          >
            {t("memo.close")}
          </Button>
        </HeaderRight>
      </Header>

      <MemoListWrapper>
        <ScrollArea><MemoListInner>
          {filteredMemos.map((lm) => (
            <MemoCard
              key={lm.localId}
              $isActive={lm.isEditing}
              $isHighlighted={!lm.isEditing && lm.memo?.id === selectedMemoId}
              onClick={() => {
                if (lm.isEditing || !lm.memo || !onMemoSelect) return;
                onMemoSelect(lm.memo.id === selectedMemoId ? null : lm.memo.id);
              }}
            >
              <MemoCardHeader>
                <MemoCardHeaderLeft>
                  {lm.isEditing ? (
                    <SegmentedButtons
                      size="xSmall"
                      variant="outlined"
                      color="primary"
                      menu={[
                        { value: "default", label: t("memo.typeDefault") },
                        { value: "adjustmentOrAddition", label: t("memo.typeAdjustment") },
                      ]}
                      value={lm.type}
                      onValueChange={(value) => {
                        if (value) handleTypeChange(lm.localId, value as CameraMemoType);
                      }}
                    />
                  ) : (
                    <>
                      <MemoTypeLabel
                        $type={lm.type}
                        variant={lm.type === "adjustmentOrAddition" ? "soft" : "filled"}
                        color="default"
                        size="xSmall"
                      >
                        {lm.type === "default"
                          ? t("memo.typeDefault")
                          : t("memo.typeAdjustment")}
                      </MemoTypeLabel>
                      {lm.memo && (
                        <>
                          <MetaSeparator />
                          <MetaText>
                            {new Date(lm.memo.updatedAt).toLocaleDateString("ko-KR", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            }).replace(/\. /g, ".").replace(/\.$/, "")}
                            {lm.memo.createdAt != null && lm.memo.createdAt !== lm.memo.updatedAt && ` ${t("memo.modified")}`}
                          </MetaText>
                        </>
                      )}
                    </>
                  )}
                </MemoCardHeaderLeft>
                <MemoCardHeaderRight>
                  {lm.isEditing ? (
                    <>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="xSmall"
                        onClick={() => handleCancelEdit(lm.localId)}
                      >
                        {t("memo.cancel")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="xSmall"
                        disabled={!hasMemoChanges(lm)}
                        onClick={() => handleSaveMemo(lm.localId)}
                      >
                        {t("memo.save")}
                      </Button>
                    </>
                  ) : (
                    <>
                      {(() => {
                        // floorPlanId 만 일치해도 positionForFloorPlan 이 비어있으면 실제로는 도면 위에 안 보이므로
                        // "배치 안 된" 상태로 취급 (도면에 배치 라벨 노출)
                        const isPlaced = lm.memo != null && (isPlacedOnContext ? isPlacedOnContext(lm.memo) : (lm.memo.floorPlanId === selectedFloorPlanId && selectedFloorPlanId != null && lm.memo.positionForFloorPlan != null));
                        const placementLabel = isPlaced
                          ? (placementLabels?.remove ?? t("memo.removeFromFloorPlan"))
                          : (placementLabels?.add ?? t("memo.showOnFloorPlan"));
                        return (
                          <Tooltip
                            trigger={
                              <IconActionButton
                                $active={isPlaced}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (lm.memo) onToggleMemoOnFloorPlan(lm.memo);
                                }}
                                aria-label={placementLabel}
                              >
                                {placeIcon ?? <Icon icon="ic_map" width={14} height={14} />}
                              </IconActionButton>
                            }
                            side="top"
                            sideOffset={4}
                          >
                            {placementLabel}
                          </Tooltip>
                        );
                      })()}
                      <Tooltip
                        trigger={
                          <IconActionButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEdit(lm.localId);
                            }}
                            aria-label={t("memo.edit")}
                          >
                            <Icon icon="ic_edit" width={14} height={14} />
                          </IconActionButton>
                        }
                        side="top"
                        sideOffset={4}
                      >
                        {t("memo.edit")}
                      </Tooltip>
                      <Tooltip
                        trigger={
                          <IconActionButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteMemo(lm.localId);
                            }}
                            aria-label={t("memo.delete")}
                          >
                            <IcDelete width={14} height={14} />
                          </IconActionButton>
                        }
                        side="top"
                        sideOffset={4}
                      >
                        {t("memo.delete")}
                      </Tooltip>
                    </>
                  )}
                </MemoCardHeaderRight>
              </MemoCardHeader>
              {lm.isEditing ? (
                <MemoTextArea
                  ref={(el) => {
                    if (el) textareaRefs.current.set(lm.localId, el);
                    else textareaRefs.current.delete(lm.localId);
                  }}
                  placeholder={t("memo.placeholder")}
                  value={lm.content}
                  onChange={(e) => handleContentChange(lm.localId, e.target.value)}
                />
              ) : (
                <MemoContent>{lm.content}</MemoContent>
              )}
            </MemoCard>
          ))}

          {filteredMemos.length === 0 && (
            <EmptyMessage>{t("memo.empty")}</EmptyMessage>
          )}
        </MemoListInner></ScrollArea>
      </MemoListWrapper>
      <DeleteConfirmDialog
        open={pendingDeleteLocalId !== null}
        title={t("dialog.deleteMemoTitle")}
        name=""
        messageKey=""
        message={t("dialog.deleteMemoMessage")}
        ns="camera-mapping"
        onCancel={() => setPendingDeleteLocalId(null)}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
}

// --- Styles ---

const MemoTypeLabel = styled(Label)<{ $type: CameraMemoType }>`
  ${({ $type }) =>
    $type === "adjustmentOrAddition" &&
    `--label-color: #EB6227;
     --label-background-color: rgba(235, 98, 39, 0.16);`}
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 12px 0 24px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
`;

const CountLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #696f8c;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MemoListWrapper = styled.div`
  flex: 1;
  min-height: 0;
`;

const MemoListInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 12px 100px 24px;
`;

const MemoCard = styled.div<{ $isActive?: boolean; $isHighlighted?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  cursor: ${({ $isActive }) => ($isActive ? "default" : "pointer")};

  ${({ $isActive, $isHighlighted }) =>
    $isActive
      ? css`
          border: 1px solid #11111f;
        `
      : $isHighlighted
        ? css`
            border: 2px solid #484BD2;
          `
        : css`
            border: 1px solid rgba(143, 149, 178, 0.32);
          `}
`;

const MemoCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
`;

const MemoCardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const MemoCardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const MetaSeparator = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(143, 149, 178, 0.24);
  flex-shrink: 0;
`;

const MetaText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: #696f8c;
  white-space: nowrap;
`;

const IconActionButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  ${({ $active }) =>
    $active
      ? css`
          color: #484BD2;
          background: rgba(72, 75, 210, 0.08);
        `
      : css`
          color: #242946;
        `}

  &:hover {
    background: rgba(49, 62, 131, 0.08);
  }
`;

const MemoTextArea = styled.textarea`
  width: 100%;
  min-height: 66px;
  padding: 8px 12px;
  border: none;
  outline: none;
  resize: none;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  background: transparent;

  &::placeholder {
    color: #8f95b2;
  }
`;

const MemoContent = styled.div`
  padding: 8px 12px;
  min-height: 66px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  white-space: pre-wrap;
  word-break: break-word;
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 12px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;
